import { useState, useEffect, useMemo, useCallback } from "react";
import React from "react";
import { UseToastProps } from "../types/toast-types";

export const useToast = ({
  duration = 3000,
  onClose,
  position,
  type,
  style = {},
}: UseToastProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (duration && duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, handleClose]);

  const types = useMemo(
    () => ({
      success: {
        backgroundColor: "#10b981",
        color: "#ffffff",
        icon: "✓",
      },
      error: {
        backgroundColor: "#ef4444",
        color: "#ffffff",
        icon: "✕",
      },
      warning: {
        backgroundColor: "#f59e0b",
        color: "#ffffff",
        icon: "⚠",
      },
      info: {
        backgroundColor: "#3b82f6",
        color: "#ffffff",
        icon: "ℹ",
      },
    }),
    []
  );

  const positions = useMemo(
    () => ({
      "top-left": { top: "20px", left: "20px" },
      "top-center": { top: "20px", left: "50%", transform: "translateX(-50%)" },
      "top-right": { top: "20px", right: "20px" },
      "bottom-left": { bottom: "20px", left: "20px" },
      "bottom-center": {
        bottom: "20px",
        left: "50%",
        transform: "translateX(-50%)",
      },
      "bottom-right": { bottom: "20px", right: "20px" },
    }),
    []
  );

  const toastStyle: React.CSSProperties = useMemo(
    () => ({
      position: "fixed",
      ...positions[position],
      ...types[type],
      padding: "16px 20px",
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      minWidth: "300px",
      maxWidth: "500px",
      zIndex: 9999,
      animation: isExiting ? "slideOut 0.3s ease-out" : "slideIn 0.3s ease-out",
      ...style,
    }),
    [position, type, isExiting, style, positions, types]
  );

  const iconDefault = types[type].icon;

  return {
    isVisible,
    handleClose,
    toastStyle,
    iconDefault,
  };
};
